/**
 * ObjectiveManualWizardPage
 *
 * Wizard de 3 pasos para configurar una campaña manualmente:
 *   1. Audiencia (segmento guardado)
 *   2. Plantilla (WhatsApp aprobada)
 *   3. Cadencia (cuándo dispara)
 *
 * Al terminar, persiste todo en el backend (PATCH /:id) y activa
 * la campaña (POST /:id/activate).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  FileText,
  Clock,
  Check,
  Loader2,
  AlertCircle,
  Zap,
  Send,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { toast } from "../../../shared/hooks/useToast";
import { objectivesApi } from "../api";
import { SegmentPicker } from "../components/SegmentPicker";
import { TemplatePicker } from "../components/TemplatePicker";
import { CadenceConfigurator } from "../components/CadenceConfigurator";
import type { Objective, ObjectiveCadence } from "../types";
import { PRESET_BY_KEY } from "../types";

const MOCK_BRAND_ID = "brand-demo";

type Step = 1 | 2 | 3;

interface StepMeta {
  num: Step;
  label: string;
  icon: typeof Users;
}

const STEPS: StepMeta[] = [
  { num: 1, label: "Audiencia", icon: Users },
  { num: 2, label: "Plantilla", icon: FileText },
  { num: 3, label: "Cadencia", icon: Clock },
];

export function ObjectiveManualWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [objective, setObjective] = useState<Objective | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [segmentId, setSegmentId] = useState<string>("");
  const [segmentName, setSegmentName] = useState<string>("");
  const [templateId, setTemplateId] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");
  const [cadence, setCadence] = useState<ObjectiveCadence | null>(null);

  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /* ─── Load campaign ─── */
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const res = await objectivesApi.getById(id);
      if (res.success) {
        const o = res.data;
        setObjective(o);
        if (o.segmentId) setSegmentId(o.segmentId);
        if (o.templateId) setTemplateId(o.templateId);
        setCadence(o.cadence);
      } else {
        setError(res.error || "No se pudo cargar la campaña");
      }
      setLoading(false);
    })();
  }, [id]);

  /* ─── Validations ─── */
  const canAdvance = useMemo(() => {
    if (step === 1) return !!segmentId;
    if (step === 2) return !!templateId;
    if (step === 3) {
      if (!cadence) return false;
      if (cadence.kind === "recurring") {
        return (cadence.daysOfWeek || []).length > 0;
      }
      if (cadence.kind === "one_shot") {
        return !!cadence.scheduledFor;
      }
    }
    return false;
  }, [step, segmentId, templateId, cadence]);

  /* ─── Save partial progress on step advance ─── */
  const handleNext = async () => {
    if (!id || !canAdvance) return;
    setSaving(true);
    setActionError(null);

    if (step === 1) {
      const res = await objectivesApi.update(id, { segmentId });
      if (!res.success) {
        setActionError(res.error || "Error al guardar");
        setSaving(false);
        return;
      }
    } else if (step === 2) {
      const res = await objectivesApi.update(id, { templateId });
      if (!res.success) {
        setActionError(res.error || "Error al guardar");
        setSaving(false);
        return;
      }
    } else if (step === 3 && cadence) {
      const res = await objectivesApi.update(id, { cadence });
      if (!res.success) {
        setActionError(res.error || "Error al guardar");
        setSaving(false);
        return;
      }

      // En el modelo de 3 tipos:
      //   - Si origin === 'custom' → enviar a revisión IA (status pending_review)
      //     y volver a /objectives con ?review=<id> para abrir el modal
      //   - Si origin === 'automatic' (edición de always-on) → activate directo
      //   - Si origin === 'suggested' (no debería pasar acá, vienen via modal) → activate
      const isCustom = objective?.origin === "custom";
      if (isCustom) {
        const submitRes = await objectivesApi.submitForReview(id);
        if (!submitRes.success) {
          setActionError(submitRes.error || "Error al enviar a revisión");
          setSaving(false);
          return;
        }
        setSaving(false);
        toast.info("Campaña enviada a revisión IA — te avisamos cuando esté lista.");
        // Vuelve al hub con ?review=<id> para que la ListPage abra el AIReviewModal
        navigate(`/objectives?review=${id}`);
        return;
      }

      // Fallback (automatic edit): activate directo
      const activateRes = await objectivesApi.activate(id);
      if (!activateRes.success) {
        setActionError(activateRes.error || "Error al activar");
        setSaving(false);
        return;
      }
      setSaving(false);
      toast.success("¡Campaña activada! Ya está corriendo.");
      navigate(`/objectives/${id}`);
      return;
    }

    setSaving(false);
    setStep((s) => (s + 1) as Step);
  };

  const handleBack = () => {
    if (step === 1) {
      navigate(`/objectives/${id}/setup`);
    } else {
      setStep((s) => (s - 1) as Step);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando…</span>
        </div>
      </div>
    );
  }

  if (error || !objective) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-red-700 font-medium mb-3">
            {error || "Campaña no encontrada"}
          </p>
          <Link
            to="/objectives"
            className="text-violet-600 text-sm hover:underline"
          >
            Volver a campañas
          </Link>
        </div>
      </div>
    );
  }

  const presetMeta = PRESET_BY_KEY[objective.preset];

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Breadcrumb */}
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <ArrowLeft size={14} />
          {step === 1 ? "Cambiar tipo de configuración" : "Paso anterior"}
        </button>

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl leading-none">{objective.emoji}</span>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {objective.name}
            </h1>
          </div>
          <p className="text-slate-500">{presetMeta.shortDescription}</p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => {
              const isActive = step === s.num;
              const isDone = step > s.num;
              return (
                <div key={s.num} className="flex-1 flex items-center">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        isDone
                          ? "bg-emerald-500 text-white"
                          : isActive
                            ? "bg-violet-600 text-white shadow-md shadow-violet-300"
                            : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {isDone ? <Check size={14} /> : <s.icon size={14} />}
                    </div>
                    <span
                      className={`text-sm font-semibold ${
                        isActive
                          ? "text-violet-700"
                          : isDone
                            ? "text-emerald-700"
                            : "text-slate-400"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-px bg-slate-200 mx-3" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-1">
                ¿A quién le hablamos?
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Elegí un segmento que ya creaste, o creá uno nuevo desde la
                pantalla de Segmentos.
              </p>
              <SegmentPicker
                brandId={MOCK_BRAND_ID}
                selectedId={segmentId}
                onSelect={(s) => {
                  setSegmentId(s.id);
                  setSegmentName(s.name || "Sin nombre");
                }}
              />
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-1">
                ¿Qué mensaje le mandamos?
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Elegí una plantilla aprobada por Meta. Si no tenés, podés
                crear una desde la pantalla de Plantillas.
              </p>
              <TemplatePicker
                brandId={MOCK_BRAND_ID}
                selectedId={templateId}
                onSelect={(t) => {
                  setTemplateId(t.id);
                  setTemplateName(t.name);
                }}
              />
            </>
          )}

          {step === 3 && cadence && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-1">
                ¿Cuándo querés que dispare?
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Definí el horario y la frecuencia. Después podés ajustar todo.
              </p>
              <CadenceConfigurator cadence={cadence} onChange={setCadence} />

              {/* Summary preview */}
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Resumen
                </p>
                <div className="space-y-1.5 text-sm">
                  <SummaryRow label="Audiencia" value={segmentName} />
                  <SummaryRow label="Plantilla" value={templateName} mono />
                  <SummaryRow
                    label="Cadencia"
                    value={
                      cadence.kind === "recurring"
                        ? `Recurrente · ${(cadence.daysOfWeek || []).length} días`
                        : "Una sola vez"
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Error */}
        {actionError && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">{actionError}</span>
          </div>
        )}

        {/* CTA bar */}
        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={saving}
          >
            <ArrowLeft size={15} />
            {step === 1 ? "Cambiar modo" : "Volver"}
          </Button>

          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!canAdvance || saving}
          >
            {saving ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                {step === 3 ? "Enviando a revisión…" : "Guardando…"}
              </>
            ) : step === 3 ? (
              objective?.origin === "custom" ? (
                <>
                  <Send size={15} />
                  Enviar a revisión IA
                </>
              ) : (
                <>
                  <Zap size={15} />
                  Activar campaña
                </>
              )
            ) : (
              <>
                Continuar
                <ArrowRight size={15} />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-component ─── */
function SummaryRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 text-xs">{label}</span>
      <span
        className={`font-semibold text-slate-900 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

export default ObjectiveManualWizardPage;
